import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { Button, CloseButton, Alert, Input, cn } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import {
  ArrowUpToLine,
  Inbox,
  Upload,
  Clock,
  ChevronRight,
  SearchIcon,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useAuth } from "@repo/context/auth/auth";
import toast from "react-hot-toast";
import { asSearchString } from "@repo/utils/search-params";
import type { TemplateListType } from "@repo/query";
import { Template } from "@/types/template_trace";
import { SafeAreaContainer } from "@/components/layout/safe-area-container";
import { Skeleton } from "@/components/ui/skeleton";
import { InfiniteScroll } from "@/components/infinite-scroll";
import React from "react";
import { ModalRealname, ModalQQBind, ModalWelcome } from "@repo/ui/pages/template_service/components/Modals";
import MinePanel from "./-components/MinePanel";
import { useBindQQ, usePendingTemplateCount, useTemplateInfiniteList } from "./-hooks/use-templates";

export type MenuTab = "received" | "uploaded" | "pending" | "mine";

export type MenuSearch = {
  tab?: MenuTab;
};

const VALID_TABS: MenuTab[] = ["received", "uploaded", "pending", "mine"];

export const Route = createFileRoute("/template_protections/")({
  validateSearch: (search: Record<string, unknown>): MenuSearch => {
    const tab = asSearchString(search.tab) as MenuTab | undefined;
    return {
      tab: tab && VALID_TABS.includes(tab) ? tab : undefined,
    };
  },
  component: TemplateProtectionMenu,
});

const menuRouteApi = getRouteApi("/template_protections/");


// 模板项骨架屏组件
const TemplateItemSkeleton = ({ index }: { index: number }) => {
  // 为不同的骨架项添加不同的宽度变化，让加载效果更自然
  const titleWidths = ["w-3/4", "w-2/3", "w-4/5", "w-3/5", "w-5/6"];
  const titleWidth = titleWidths[index % titleWidths.length];

  return (
    <div className="w-full border-b border-[#f3f3f3] last:border-b-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-4 gap-3 sm:gap-0">
        <div className="flex flex-row gap-2 items-center">
          {/* 预览图骨架 */}
          <Skeleton className="w-16 h-16 rounded-lg shrink-0" />
          <div className="flex flex-col gap-2.5 items-start justify-start flex-1 min-w-0">
            {/* 标题骨架 - 模拟模板标题，使用不同宽度 */}
            <Skeleton className={`h-5 ${titleWidth} rounded-lg`} />
            {/* 信息行骨架 - 模拟文件数量、时间、实名保护状态 */}
            <div className="flex flex-row text-sm gap-2.5 items-start justify-start flex-wrap">
              <Skeleton className="h-4 w-16 rounded" />
              <Skeleton className="h-4 w-32 rounded" />
              {/* 随机显示实名保护标签 */}
              {index % 3 === 0 && <Skeleton className="h-4 w-20 rounded" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 模板列表骨架屏组件
const TemplateListSkeleton = () => {
  return (
    <div>
      {Array.from({ length: 5 }).map((_, index) => (
        <TemplateItemSkeleton key={index} index={index} />
      ))}
    </div>
  );
};

// 计算并返回过期时间显示文本
const getExpireTimeText = (
  expireAt: number,
  saved?: boolean,
): { text: string; color: string } => {
  if (saved) {
    return { text: "永久有效", color: "text-green-500" };
  }

  const now = Date.now() / 1000; // 当前时间戳（秒）
  const remainingSeconds = expireAt - now;

  // 已过期
  if (remainingSeconds <= 0) {
    return { text: "已过期", color: "text-gray-500" };
  }

  const remainingHours = remainingSeconds / 3600;

  // 小于24小时
  if (remainingHours < 24) {
    const hours = Math.floor(remainingHours);
    return { text: `${hours}小时后过期`, color: "text-red-500" };
  }

  // 大于等于1天
  const days = Math.floor(remainingHours / 24);
  return { text: `${days}天后过期`, color: "text-orange-500" };
};


// 模板项组件
const TemplateItem = ({
  template,
  onDetail,
}: {
  template: Template;
  onDetail: (id: string, sendId?: string) => void;
  onReceiveSuccess?: () => void;
}) => {
  // 获取过期时间显示信息
  const expireInfo =
    template.send &&
      template.send.received_at !== 0 &&
      (template.saved || template.send.expire_at)
      ? getExpireTimeText(template.send.expire_at, template.saved)
      : null;
  return (
    <div
      className="w-full border-b border-[#f3f3f3] last:border-b-0 hover:bg-gray-100 transition-all cursor-pointer"
      onClick={() => onDetail(template.id, template.send?.send_id)}
    >
      <div className="flex items-center justify-between px-4 py-4 gap-3 sm:gap-0 flex-nowrap">
        <div className="flex flex-row gap-2 items-center">
          {/* 预览图 */}
          <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-gray-100">
            {template.preview_url ? (
              <img
                src={template.preview_url}
                alt={template.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // 图片加载失败时显示占位符
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.innerHTML =
                    '<div class="w-full h-full flex items-center justify-center text-gray-400 text-xs">无预览</div>';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                无预览
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2.5 items-start justify-start flex-1 min-w-0">
            <div className="font-semibold text-base truncate w-full">
              {template.title}
              {template.must_realname && (
                <ShieldCheck size={16} className="text-orange-500 inline-block ml-1 mb-1" />
              )}
            </div>
            <div className="flex flex-row text-sm gap-2.5 items-start justify-start text-gray-400 flex-wrap">
              <div>
                {new Date(template.created_at * 1000).toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </div>
              {expireInfo && (
                <span className={expireInfo.color}>{expireInfo.text}</span>
              )}
            </div>
          </div>
        </div>
        <ChevronRight className="w-4 h-4" />
      </div>
    </div>
  );
};

// 底部导航栏组件
const BottomBar = ({
  activeTab,
  onTabChange,
  pendingCount,
}: {
  activeTab: string;
  onTabChange: (tab: string) => void;
  pendingCount: number;
}) => {
  const menuItems = [
    { key: "received", label: "我收到的", icon: Inbox },
    { key: "uploaded", label: "我上传的", icon: Upload },
    { key: "pending", label: `待签收${pendingCount > 0 ? ` (${pendingCount})` : ""}`, icon: Clock },
    { key: "mine", label: "我的", icon: UserRound },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-5">
      <div className="flex items-center justify-between px-2 py-1">
        {/* 导航菜单项 */}
        <div className="flex items-center justify-center flex-1 gap-0">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.key}
                onClick={() => onTabChange(item.key)}
                className={`flex flex-col items-center justify-center px-1 py-2 rounded-lg transition-all duration-200 min-w-0 flex-1 ${activeTab === item.key
                  ? "text-primary scale-105"
                  : "text-gray-600 hover:text-gray-800"
                  }`}
              >
                <div className="relative">
                  <IconComponent size={18} />
                </div>
                <span className="text-[10px] font-medium truncate mt-0.5 leading-tight">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// localStorage 管理函数
const DONT_BOTHER_KEYS = {
  REALNAME_ALERT: "dont_bother_realname_alert",
  QQ_ALERT: "dont_bother_qq_alert",
} as const;

const setDontBotherPreference = (key: string) => {
  localStorage.setItem(key, "true");
};

const getDontBotherPreference = (key: string): boolean => {
  return localStorage.getItem(key) === "true";
};

// 主组件
export default function TemplateProtectionMenu() {
  const { tab: tabParam } = menuRouteApi.useSearch();
  const routeNavigate = menuRouteApi.useNavigate();
  const activeTab: MenuTab = tabParam ?? "received";
  const isMineTab = activeTab === "mine";

  const setActiveTab = useCallback(
    (tab: string) => {
      void routeNavigate({
        search: (prev) => ({
          ...prev,
          tab: (tab as MenuTab) === "received" ? undefined : (tab as MenuTab),
        }),
      });
    },
    [routeNavigate],
  );

  const { isAuthenticated, userInfo } = useAuth();
  const navigate = useNavigate();
  const [showRealnameAlert, setShowRealnameAlert] = useState(false);
  const [showQQAlert, setShowQQAlert] = useState(false);
  // 输入框内容与「已提交的搜索词」分开：只有回车 / 提交才进 query key 触发请求
  const [searchValue, setSearchValue] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const PAGE_SIZE = 10;

  const listEnabled = isAuthenticated === true && !isMineTab;
  const {
    data: listData,
    error: listError,
    isPending: listPending,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useTemplateInfiniteList({
    type: activeTab as TemplateListType,
    pageSize: PAGE_SIZE,
    search: submittedSearch,
    enabled: listEnabled,
  });
  const templates: Template[] = listData?.pages.flatMap((p) => p.content) ?? [];
  // 未登录 / 我的 Tab 时 isPending 恒为 true，这里按「不请求」处理成非加载态
  const loadingData = listEnabled && (listPending || isFetchingNextPage);
  const hasMore = hasNextPage ?? false;

  useEffect(() => {
    if (listError) {
      toast.error((listError as { msg?: string }).msg || "加载失败，请重试");
    }
  }, [listError]);

  const { data: pendingCount = 0 } = usePendingTemplateCount(isAuthenticated === true);
  const bindQQMutation = useBindQQ();

  // 初始化localStorage状态
  useEffect(() => {
    setShowRealnameAlert(
      !getDontBotherPreference(DONT_BOTHER_KEYS.REALNAME_ALERT),
    );
    setShowQQAlert(!getDontBotherPreference(DONT_BOTHER_KEYS.QQ_ALERT));
  }, []);

  // 处理"不要烦我"点击
  const handleDontBotherRealname = () => {
    setDontBotherPreference(DONT_BOTHER_KEYS.REALNAME_ALERT);
    setShowRealnameAlert(false);
  };

  const handleDontBotherQQ = () => {
    setDontBotherPreference(DONT_BOTHER_KEYS.QQ_ALERT);
    setShowQQAlert(false);
  };

  // 处理QQ绑定：ModalQQBind 靠 Promise reject 保持弹窗打开，所以用 mutateAsync 把错误抛回去
  const handleQQBind = (qq: string) => bindQQMutation.mutateAsync(qq).then(() => undefined);

  // 当切换标签页时，清空搜索框（已提交的搜索词一起清掉，列表回到无搜索状态）
  useEffect(() => {
    setSearchValue("");
    setSubmittedSearch("");
  }, [activeTab]);

  // 加载更多数据：由 useInfiniteQuery 负责页码，这里只做防重与守卫
  const loadMoreData = useCallback(() => {
    if (!listEnabled || loadingData || !hasMore) {
      return;
    }
    void fetchNextPage();
  }, [listEnabled, loadingData, hasMore, fetchNextPage]);

  const handleDetail = (id: string, sendId?: string) => {
    navigate({
      to: "/template_protections/detail/$templateId",
      params: { templateId: id },
      search: { sendId },
    });
  };


  const handleJumpToUpload = () => {
    if (!isAuthenticated) {
      return;
    }
    if (!userInfo?.real_id.valid) {
      toast.error("请先完成实名认证");
      return;
    }
    navigate({ to: "/template_protections/upload" });
  };

  // 执行搜索：提交搜索词进 query key，列表自动从第一页重新加载，加载更多也会带上搜索条件
  const handleSearch = useCallback(() => {
    // 如果未认证或没有激活的标签页，不执行搜索
    if (!isAuthenticated || !activeTab || isMineTab) {
      return;
    }
    setSubmittedSearch(searchValue.trim());
  }, [isAuthenticated, activeTab, isMineTab, searchValue]);

  // 处理回车键搜索
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // 处理表单提交（移动端键盘搜索按钮）
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSearch();
  };

  return (
    <>
      <SafeAreaContainer
        className={cn("relative", isMineTab ? "bg-background" : "bg-surface")}
        customPaddingBottom="6rem"
      >
        <div className="flex flex-col lg:gap-2.5 items-start justify-start">
          {!isMineTab && (
            <div className="flex flex-col md:flex-row gap-2 w-full">
              {isAuthenticated && !userInfo?.real_id.valid && showRealnameAlert && (
                <Alert status="warning">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>您尚未完成实名认证</Alert.Title>
                    <Alert.Description className="mb-2">
                      完成实名认证可开启标记机模板上传权限以及签收要求实名认证的模板。
                    </Alert.Description>
                    <ModalRealname />
                  </Alert.Content>
                  <CloseButton onPress={handleDontBotherRealname} />
                </Alert>
              )}
              {isAuthenticated && !userInfo?.qq && showQQAlert && (
                <Alert>
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>您尚未绑定QQ号码</Alert.Title>
                    <Alert.Description className="mb-2">
                      绑定QQ号码可使用QQ号来接收模板。
                    </Alert.Description>
                    <ModalQQBind onSave={handleQQBind} />
                  </Alert.Content>
                  <CloseButton onPress={handleDontBotherQQ} />
                </Alert>
              )}
            </div>
          )}

          {isMineTab ? (
            <MinePanel />
          ) : (
            <>
              <form className="px-4 pt-4 w-full" onSubmit={handleSubmit}>
                <div className="relative">
                  <SearchIcon
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-default-400 shrink-0 pointer-events-none"
                  />
                  <Input
                    placeholder="搜索模板"
                    type="search"
                    enterKeyHint="search"
                    disabled={loadingData}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="pl-9 rounded-full"
                    autoComplete="off"
                    fullWidth
                  />
                </div>
              </form>
              {/* 模板列表 */}
              <div className="flex-1 flex flex-col rounded-lg p-4 w-full gap-2">
                <InfiniteScroll
                  loading={loadingData}
                  hasMore={hasMore}
                  onLoadMore={loadMoreData}
                  loadingText="正在努力加载"
                  noMoreText="没有更多了"
                >
                  {templates && templates.length > 0 ? (
                    templates.map((template) => (
                      <TemplateItem
                        key={template.id}
                        template={template}
                        onDetail={handleDetail}
                      />
                    ))
                  ) : !loadingData ? (
                    <div className="flex items-center justify-center py-12 text-gray-500">
                      <div className="text-center">
                        <div className="text-lg font-medium mb-2">暂无模板</div>
                        <div className="text-sm">当前分类下没有找到模板文件</div>
                      </div>
                    </div>
                  ) : (
                    <TemplateListSkeleton />
                  )}
                </InfiniteScroll>
              </div>
              {/* 上传按钮 */}
              <Button
                className="fixed bottom-24 right-6 bg-primary text-white rounded-full ml-1 shrink-0 shadow-lg hover:shadow-xl"
                onPress={handleJumpToUpload}
              >
                <ArrowUpToLine size={16} />
                上传模板
              </Button>
            </>
          )}
        </div>

        {/* 底部导航栏 */}
        <BottomBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          pendingCount={pendingCount}
        />
      </SafeAreaContainer>
      <ModalWelcome />
    </>
  );
}
